/**
 * Données du questionnaire Autodiagnostic
 * 60 questions réparties en 15 compétences (4 questions par compétence)
 */

export const AUTODIAG_QUESTIONS_PER_PAGE = 1;

export interface AutodiagOption {
  value: number;
  label: string;
  description: string;
}

export const autodiagOptions: AutodiagOption[] = [
  {
    value: 1,
    label: "faible maîtrise",
    description:
      "indique que l'aptitude est peu présente et nécessite d'être travaillée",
  },
  {
    value: 2,
    label: "maîtrise moyenne",
    description:
      "indique que l'aptitude se manifeste positivement dans certains contextes et pas dans d'autres, elle manque de constance",
  },
  {
    value: 3,
    label: "bonne maîtrise",
    description:
      "indique que l'aptitude se manifeste régulièrement quel que soit le contexte, c'est une aptitude plutôt forte",
  },
  {
    value: 4,
    label: "maîtrise excellente",
    description:
      "indique que l'aptitude est de grande qualité , présente quel que soit le contexte avec une forte intensité, c'est une aptitude très forte qui \"saute aux yeux de tous\".",
  },
];

export interface AutodiagQuestion {
  titre?: string;
  name: string;
  label: string;
  sous_questions?: AutodiagQuestion[];
}

export const autodiagQuestions: AutodiagQuestion[] = [
  {
    titre: "Analyse et synthèse",
    name: "analyse_synthese",
    label:
      "Appréciation globale de la compétence\n« Comment j'appréhende et je comprends les éléments d'une situation ? ».",
    sous_questions: [
      {
        name: "analyse_synthese_1",
        label:
          "Vous recherchez l'information et dégagez les éléments essentiels d'une situation afin d'en tirer les meilleures conclusions ou solutions.",
      },
      {
        name: "analyse_synthese_2",
        label:
          "Vous avez beaucoup de rigueur dans ce que vous dites ou faites, vous pesez le pour et le contre afin d'en mesurer les conséquences.",
      },
      {
        name: "analyse_synthese_3",
        label:
          "Vous prenez systématiquement du recul pour préparer vos décisions.",
      },
    ],
  },
  {
    titre: "Adaptabilité",
    name: "adaptabilite",
    label:
      "Appréciation globale de la compétence\n« Comment je m'ajuste aux situations nouvelles ? ».",
    sous_questions: [
      {
        name: "adaptabilite_1",
        label: "Vous êtes souple et recherchez toujours le compromis.",
      },
      {
        name: "adaptabilite_2",
        label: "Vous êtes ouvert aux idées différentes des vôtres.",
      },
      {
        name: "adaptabilite_3",
        label:
          "Vous trouvez toujours des solutions pour faire adopter le changement, même si vous vous confrontez à des résistances, des conflits, des difficultés.",
      },
    ],
  },
  {
    titre: "Sens de l'innovation",
    name: "sens_innovation",
    label:
      "Appréciation globale de la compétence\n« Que puis-je apporter de nouveau et suis-je sensible à l'innovation ? ».",
    sous_questions: [
      {
        name: "sens_innovation_1",
        label:
          "Vous savez encouragez votre équipe à prendre des initiatives, les nouvelles idées sont les bienvenues et sont facilement prises en compte.",
      },
      {
        name: "sens_innovation_2",
        label:
          "Vous êtes force de proposition et à la recherche de solutions nouvelles.",
      },
      {
        name: "sens_innovation_3",
        label:
          "Vous savez exploiter la critique de certains membres de l'équipe pour développer la créativité et l'innovation.",
      },
    ],
  },
  {
    titre: "Contrôle de soi",
    name: "controle_soi",
    label:
      "Appréciation globale de la compétence\n« Comment je reste en contrôle ? ».",
    sous_questions: [
      {
        name: "controle_soi_1",
        label:
          "Vous savez où vous en êtes en situation perturbante et maintenez un équilibre, du calme dans vos relations.",
      },
      {
        name: "controle_soi_2",
        label:
          "Vous savez garder le cap sur vos priorités même si vous êtes dans des contraintes fortes de temps, de stress, de tensions.",
      },
      {
        name: "controle_soi_3",
        label:
          "Vous savez vous maîtriser dans des situations stressantes ou difficiles en ne montrant pas vos émotions.",
      },
    ],
  },
  {
    titre: "Compréhension des autres",
    name: "comprehension_autres",
    label:
      "Appréciation globale de la compétence\n« Ai-je le sens des relations interpersonnelles ? ».",
    sous_questions: [
      {
        name: "comprehension_autres_1",
        label:
          "Vous avez le sens de l'écoute active et savez faire ressortir les éléments essentiels, en cherchant à comprendre les réalités de l'autre. ",
      },
      {
        name: "comprehension_autres_2",
        label:
          "Vous savez faire face à l'adversité et à la confrontation en développant des comportements de qualité, en étant attentif à l'autre.",
      },
      {
        name: "comprehension_autres_3",
        label: "Vous communiquez généralement avec empathie.",
      },
    ],
  },
  {
    titre: "Communication orale et écrite",
    name: "communication",
    label:
      "Appréciation globale de la compétence\n« Comment je transmets un message ? ».",
    sous_questions: [
      {
        name: "communication_1",
        label: "Vous savez communiquer avec dynamisme et clarté.",
      },
      {
        name: "communication_2",
        label:
          "Vous maîtrisez votre posture et votre gestuelle en sachant l'adapter aux différentes situations.",
      },
      {
        name: "communication_3",
        label:
          "Vous favorisez la transmission d'information avec vos collègues et/ou votre équipe par différents moyens (réunions, mails, notes de services, forum, pots…).",
      },
    ],
  },
  {
    titre: "Coopération et esprit d'équipe",
    name: "cooperation_esprit_equipe",
    label:
      "Appréciation globale de la compétence\n« Comment je construis le climat de confiance dans l'équipe en favorisant le travail collaboratif ? » .",
    sous_questions: [
      {
        name: "cooperation_esprit_equipe_1",
        label: "Il vous est facile de travailler en équipe.",
      },
      {
        name: "cooperation_esprit_equipe_2",
        label:
          "Vous faciliter les interactions entre les membres de l'équipe et/ou des collègues pour prendre en compte leurs idées et points de vue.",
      },
      {
        name: "cooperation_esprit_equipe_3",
        label:
          "Vous faites confiance aux membres de votre équipe pour travailler sur les objectifs communs de votre entité d'appartenance.",
      },
    ],
  },
  {
    titre: "Sens des résultats",
    name: "sens_resultats",
    label:
      "Appréciation globale de la compétence « Comment j'amène mon équipe à produire des résultats ? ».",
    sous_questions: [
      {
        name: "sens_resultats_1",
        label:
          "Vous définissez clairement les objectifs à atteindre en fonction des priorités organisationnelles.",
      },
      {
        name: "sens_resultats_2",
        label: "Vous suivez les résultats en optimisant les ressources.",
      },
      {
        name: "sens_resultats_3",
        label:
          "Vous évaluez les résultats et recherchez des solutions visant à améliorer les processus.",
      },
    ],
  },
  {
    titre: "Organisation et coordination",
    name: "organisation_coordination",
    label:
      "Appréciation globale de la compétence\n« Comment j'organise et contrôle l'activité de l'équipe ? ».",
    sous_questions: [
      {
        name: "organisation_coordination_1",
        label:
          "Vous planifiez et organisez efficacement les activités de l'équipe par un échéancier.",
      },
      {
        name: "organisation_coordination_2",
        label:
          "Vous mettez en œuvre des process, des méthodes permettant la réalisation de vos décisions.",
      },
      {
        name: "organisation_coordination_3",
        label:
          "Vous pratiquez régulièrement le contrôle de l'activité de l'équipe par des comptes-rendus réguliers, des visites fréquentes ou autres formes de pilotage.",
      },
    ],
  },
  {
    titre: "Prévention et sensibilité à la sécurité",
    name: "prevention_sensibilite_securite",
    label:
      "Appréciation globale de la compétence\n« Comment je communique et pilote « la sécurité » au sein de mon équipe ? ».",
    sous_questions: [
      {
        name: "prevention_sensibilite_securite_1",
        label:
          "Vous respectez et faites respecter les consignes et règles de sécurité.",
      },
      {
        name: "prevention_sensibilite_securite_2",
        label:
          "Vous communiquez auprès des équipes des règles de sécurité par des actions de sensibilisation, en adéquation avec les valeurs d'ENEDIS. ",
      },
      {
        name: "prevention_sensibilite_securite_3",
        label:
          "Vous organisez les actions nécessaires au « management de la sécurité » en vous attachant à piloter leur suivi dans le temps ( reporting, contrôle des activités déléguées…). ",
      },
    ],
  },
  {
    titre: "Force de persuasion",
    name: "force_persuasion",
    label:
      "Appréciation globale de la compétence\n« Comment j'influence mon équipe ? ».",
    sous_questions: [
      {
        name: "force_persuasion_1",
        label:
          "Vous agissez avec enthousiasme et détermination pour amener votre équipe aux résultats attendus.",
      },
      {
        name: "force_persuasion_2",
        label:
          "Vous savez argumenter vos idées et propositions dans l'objectif de stimuler la motivation de l'équipe.",
      },
      {
        name: "force_persuasion_3",
        label:
          "Vous aimez convaincre les autres de votre point de vue, réussir à les persuader pour les faire adhérer à vos idées, à vos projets. ",
      },
    ],
  },
  {
    titre: "Développement des compétences",
    name: "developpement_competences",
    label:
      "Appréciation globale de la compétence \n« Comment je développe mes collaborateurs ? ».",
    sous_questions: [
      {
        name: "developpement_competences_1",
        label:
          "Vous aidez chaque membre de votre équipe à identifier sa performance actuelle, ses points forts et ses points faibles associés aux résultats.",
      },
      {
        name: "developpement_competences_2",
        label:
          "Vous donnez régulièrement un feedback à chacun sur sa performance et ses progrès.",
      },
      {
        name: "developpement_competences_3",
        label:
          "Vous valorisez vos collaborateurs par des missions de délégation, par la formation et des conseils personnalisés dans l'objectif de les faire évoluer.",
      },
    ],
  },
  {
    titre: "Délégation",
    name: "delegation",
    label:
      "Appréciation globale de la compétence\n« Comment je donne un objectif, comment je le suis et le contrôle ? ».",
    sous_questions: [
      {
        name: "delegation_1",
        label:
          "Vous identifiez les collaborateurs au regard des tâches déléguées et leur donner des objectifs clairs et précis.",
      },
      {
        name: "delegation_2",
        label:
          "Vous donnez régulièrement un feedback à chacun sur sa performance et ses progrès.",
      },
      {
        name: "delegation_3",
        label:
          "Vous organiser le suivi et le contrôle de la délégation (points réguliers et évaluation finale).",
      },
    ],
  },
  {
    titre: "Vision stratégique",
    name: "vision_strategique",
    label:
      "Appréciation globale de la compétence\n« Comment je partage la vision stratégique et les valeurs de mon entreprise ? ».",
    sous_questions: [
      {
        name: "vision_strategique_1",
        label:
          "Vous avez une bonne idée de la stratégie de l'entreprise et vous savez la faire partager de manière claire à votre équipe.",
      },
      {
        name: "vision_strategique_2",
        label:
          "Vous véhiculez les valeurs de l'entreprise et vous cherchez à les faire partager à votre équipe.",
      },
      {
        name: "vision_strategique_3",
        label:
          "Vous avez une posture qui défend l'image et les valeurs de l'entreprise.",
      },
    ],
  },
  {
    titre: "Leadership",
    name: "leadership",
    label:
      "Appréciation globale de la performance \n« Comment j'ajuste mon style de management au regard de mes collaborateurs ? ».",
    sous_questions: [
      {
        name: "leadership_1",
        label:
          "Vous avez une bonne lecture de la réalité en ajustant vos comportements à la situation.",
      },
      {
        name: "leadership_2",
        label:
          "Vous dégagez de l'énergie, de l'enthousiasme par vos idées, votre posture, votre communication.",
      },
      {
        name: "leadership_3",
        label:
          "Vous savez ajuster votre style de management en fonction du niveau de « maturité » des collaborateurs (supervision serrée ou pas).",
      },
    ],
  },
];
